import dayjs from "dayjs";
import { jsPDF } from "jspdf";

import type { InternalTransferGuideData } from "~/types/productTransfer";

const LOGO_URL =
  "https://mdrvhekhimhcwydrpueo.supabase.co/storage/v1/object/public/uploads/assets/aliace-logo.jpg";

const formatNumber = (value: number): string =>
  new Intl.NumberFormat("es-CL").format(Math.round(value));

// Load image and convert to base64 (same helper shape as quotationPdf.ts).
const loadImage = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });

// Printable "Guía de Traslado Interno" for any traslado (producto terminado or
// ingredientes). Opens the PDF in a new tab. Internal document only — not a tax document.
export async function generateInternalTransferPdf(
  data: InternalTransferGuideData
): Promise<void> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;

  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImage(LOGO_URL);
  } catch (e) {
    console.warn("Could not load logo:", e);
  }

  // ==================== HEADER ====================
  let y = 12;

  if (logoBase64) {
    doc.addImage(logoBase64, "JPEG", margin, y, 40, 20);
  }

  const centerX = pageWidth / 2;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("ALIMENTOS Y ACEITES SPA", centerX, y + 4, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text("GUÍA DE TRASLADO INTERNO", centerX, y + 9, { align: "center" });
  doc.text("Documento interno de movimiento de bodega", centerX, y + 13, {
    align: "center",
  });

  // Transfer number box on the right
  const boxX = pageWidth - margin - 45;
  const boxWidth = 45;
  const boxHeight = 22;
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(boxX, y, boxWidth, boxHeight);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("TRASLADO", boxX + boxWidth / 2, y + 7, { align: "center" });
  doc.setFontSize(12);
  doc.text(data.transferNumber, boxX + boxWidth / 2, y + 15, {
    align: "center",
  });

  y += 30;

  // ==================== INFO BOX ====================
  const infoBoxY = y;
  const infoBoxHeight = 30;
  doc.setLineWidth(0.3);
  doc.rect(margin, infoBoxY, pageWidth - 2 * margin, infoBoxHeight);
  const dividerX = pageWidth / 2 + 15;
  doc.line(dividerX, infoBoxY, dividerX, infoBoxY + infoBoxHeight);

  y += 6;
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("ORIGEN:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(data.fromLabel, margin + 35, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("DESTINO:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(data.toLabel, margin + 35, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("REALIZADO POR:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(data.performedBy, margin + 35, y);

  let rightY = infoBoxY + 6;
  const rightX = dividerX + 3;
  doc.setFont("helvetica", "bold");
  doc.text("FECHA:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(dayjs(data.transferredAt).format("DD/MM/YYYY HH:mm"), rightX + 30, rightY);

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("NOTAS:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  const notes = data.notes ?? "-";
  doc.text(notes.length > 32 ? notes.substring(0, 29) + "..." : notes, rightX + 30, rightY);

  // ==================== LINES TABLE ====================
  y = infoBoxY + infoBoxHeight + 6;
  const tableY = y;
  const colWidths = [26, 84, 20, 30, 30];
  const colHeaders = ["CÓDIGO", "ÍTEM", "UM", "LOTE", "CANTIDAD"];
  const colX: number[] = [margin];
  for (let i = 1; i < colWidths.length; i++) {
    colX.push(colX[i - 1] + colWidths[i - 1]);
  }

  doc.setFillColor(240, 240, 240);
  doc.rect(margin, tableY, pageWidth - 2 * margin, 6, "F");
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(margin, tableY, pageWidth - 2 * margin, 6);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  colHeaders.forEach((header, i) => doc.text(header, colX[i] + 1, tableY + 4));
  colX.forEach((x) => doc.line(x, tableY, x, tableY + 6));
  doc.line(pageWidth - margin, tableY, pageWidth - margin, tableY + 6);

  y = tableY + 6;
  const rowHeight = 7;
  const maxRows = Math.max(data.lines.length, 6);
  const bodyHeight = maxRows * rowHeight;
  doc.rect(margin, y, pageWidth - 2 * margin, bodyHeight);
  colX.forEach((x) => doc.line(x, y, x, y + bodyHeight));
  doc.line(pageWidth - margin, y, pageWidth - margin, y + bodyHeight);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  data.lines.forEach((line, index) => {
    const itemY = y + 5 + index * rowHeight;
    if (index > 0) {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y + index * rowHeight, pageWidth - margin, y + index * rowHeight);
      doc.setDrawColor(0);
    }
    doc.text(line.codigo.substring(0, 12), colX[0] + 1, itemY);
    const item = line.item.length > 48 ? line.item.substring(0, 45) + "..." : line.item;
    doc.text(item, colX[1] + 1, itemY);
    doc.text(line.um, colX[2] + 1, itemY);
    doc.text(line.lote.substring(0, 16), colX[3] + 1, itemY);
    doc.text(formatNumber(line.cantidad), colX[4] + 1, itemY);
  });

  // ==================== SIGNATURE FOOTER ====================
  y = tableY + 6 + bodyHeight + 24;
  const sigWidth = (pageWidth - 2 * margin - 20) / 2;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + sigWidth, y);
  doc.line(pageWidth - margin - sigWidth, y, pageWidth - margin, y);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text("Entregado por (origen)", margin + sigWidth / 2, y + 5, {
    align: "center",
  });
  doc.text("Recibido y verificado por (destino)", pageWidth - margin - sigWidth / 2, y + 5, {
    align: "center",
  });

  doc.setFontSize(5);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Documento generado el ${dayjs().format("DD/MM/YYYY HH:mm")} — uso interno, no es documento tributario.`,
    pageWidth / 2,
    y + 16,
    { align: "center" }
  );

  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
}
